import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import './Widget.css';

interface TaxRevenue {
  period: 'daily' | 'weekly' | 'monthly';
  collected: number;      // Total tax collected
  reinvested: number;     // Tax allocated to buys
  available: number;      // Tax available for buys
  startDate: number;
  endDate: number;
  trades: number;         // Number of trades
}

type Period = 'daily' | 'weekly' | 'monthly';

export const TaxRevenue: React.FC = () => {
  const [period, setPeriod] = useState<Period>('daily');
  const [report, setReport] = useState<TaxRevenue | null>(null);
  const [totalAccumulated, setTotalAccumulated] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.taxRevenue.getReport(period);
        setReport(response.report);
        setTotalAccumulated(response.totalAccumulated);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tax data');
        console.error('Tax revenue fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [period]);

  const formatUSD = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  if (loading) {
    return (
      <div className="widget tax-revenue">
        <h3>Tax Revenue (5%)</h3>
        <div className="widget-loading">Loading...</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="widget tax-revenue">
        <h3>Tax Revenue (5%)</h3>
        <div className="widget-error">{error || 'No data available'}</div>
      </div>
    );
  }

  const allocationPercentage =
    report.collected > 0 ? ((report.reinvested / report.collected) * 100).toFixed(1) : '0.0';

  return (
    <div className="widget tax-revenue">
      <div className="widget-header">
        <h3>Tax Revenue (5%)</h3>
        <div className="period-selector">
          <button
            className={period === 'daily' ? 'active' : ''}
            onClick={() => setPeriod('daily')}
          >
            Daily
          </button>
          <button
            className={period === 'weekly' ? 'active' : ''}
            onClick={() => setPeriod('weekly')}
          >
            Weekly
          </button>
          <button
            className={period === 'monthly' ? 'active' : ''}
            onClick={() => setPeriod('monthly')}
          >
            Monthly
          </button>
        </div>
      </div>

      <div className="tax-summary">
        <div className="summary-item total-accumulated">
          <label>Total Accumulated</label>
          <div className="value highlight">{formatUSD(totalAccumulated)}</div>
        </div>

        <div className="summary-item period-tax">
          <label>{period.charAt(0).toUpperCase() + period.slice(1)} Tax</label>
          <div className="value">{formatUSD(report.collected)}</div>
        </div>
      </div>

      <div className="tax-breakdown">
        <div className="breakdown-item">
          <div className="breakdown-header">
            <span className="label">Allocated to Buys</span>
            <span className="percentage">{allocationPercentage}%</span>
          </div>
          <div className="breakdown-bar">
            <div
              className="bar-fill allocated-fill"
              style={{ width: `${allocationPercentage}%` }}
            />
          </div>
          <div className="breakdown-value">{formatUSD(report.reinvested)}</div>
        </div>

        <div className="breakdown-item">
          <div className="breakdown-header">
            <span className="label">Available for Buys</span>
            <span className="percentage">
              {report.collected > 0
                ? ((report.available / report.collected) * 100).toFixed(1)
                : '0.0'}
              %
            </span>
          </div>
          <div className="breakdown-bar">
            <div
              className="bar-fill unallocated-fill"
              style={{
                width: `${
                  report.collected > 0
                    ? ((report.available / report.collected) * 100).toFixed(1)
                    : '0.0'
                }%`,
              }}
            />
          </div>
          <div className="breakdown-value">{formatUSD(report.available)}</div>
        </div>
      </div>

      <div className="tax-stats">
        <div className="stat-item">
          <label>Trades Count</label>
          <div className="value">{report.trades}</div>
        </div>
        <div className="stat-item">
          <label>Avg Tax/Trade</label>
          <div className="value">{formatUSD(report.trades > 0 ? report.collected / report.trades : 0)}</div>
        </div>
      </div>

      <div className="tax-info">
        <div className="info-badge">
          <span className="info-icon">ℹ</span>
          <span className="info-text">
            5% of all trade value is collected as tax and reinvested to increase buying power
          </span>
        </div>
      </div>
    </div>
  );
};
